import { Link } from 'react-router-dom';
import { PanelLeftOpenIcon, SearchIcon } from 'lucide-react';
import { BrandMark } from '@/components/layout/brand-mark';

import { Button } from '@/components/ui/button';
import { CampaignAwareAgentPanel } from '@/components/content-hub/campaign-agent-panel';
import { ProfileMenu } from '@/components/layout/profile-menu';
import { TenantEntityIndicator } from '@/components/access/tenant-entity-switcher';
import { openCommandPalette } from '@/components/focus-queue/command-palette';

export function Header({
  onMobileSidebarOpen,
  showAgent
}: {
  onMobileSidebarOpen: () => void;
  showAgent: boolean;
}) {
  return (
    <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 border-border z-40 flex min-h-14 shrink-0 items-center border-b pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] backdrop-blur">
      {/* Brand rail — fixed width, independent of the sidebar collapse state */}
      <div className="hidden h-full w-60 shrink-0 items-center px-4 md:flex">
        <Link
          to="/"
          className="focus-visible:ring-ring tap-target flex min-w-0 items-center gap-2 rounded-md font-semibold focus-visible:ring-2 focus-visible:outline-none"
        >
          <BrandMark />
          <span className="sr-only truncate text-sm tracking-tight sm:not-sr-only">Relationship Foundry</span>
        </Link>
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-1 px-2 sm:gap-2 md:px-4">
        {/* Mobile: open the navigation sidebar (same PanelLeft icon as the desktop collapse toggle) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileSidebarOpen}
          aria-label="Open sidebar"
          className="-ml-1 md:hidden"
        >
          <PanelLeftOpenIcon className="size-5" />
        </Button>

        {/* Mobile brand (sidebar and brand rail are hidden below md) */}
        <Link
          to="/"
          className="focus-visible:ring-ring tap-target flex items-center gap-2 rounded-md font-semibold focus-visible:ring-2 focus-visible:outline-none hidden sm:flex md:hidden"
        >
          <BrandMark />
          <span className="sr-only text-sm tracking-tight sm:not-sr-only">Relationship Foundry</span>
        </Link>

        <TenantEntityIndicator />

        {showAgent ? (
          <button
            type="button"
            onClick={openCommandPalette}
            aria-label="Search"
            aria-keyshortcuts="Control+K Meta+K"
            title="Search records and pages (⌘K)"
            className="border-input bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring mx-auto flex h-9 min-w-9 shrink items-center gap-2 rounded-md border px-2.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none sm:w-full sm:max-w-md"
          >
            <SearchIcon className="size-4 shrink-0" aria-hidden />
            <span className="hidden flex-1 truncate text-left sm:inline">Search</span>
          </button>
        ) : null}

        <div className="ml-auto flex min-w-0 items-center gap-2">
          {showAgent ? <CampaignAwareAgentPanel /> : null}
          <ProfileMenu />
        </div>
      </div>
    </header>
  );
}
